import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';

// Components
import UserProfile from '../components/UserProfile';
import { URCodeFooter } from '../components/URCodeFooter';
import ConfirmModal from '../components/ConfirmModal';

// API & Utils
import { 
  getOwnerProductsAPI, 
  deleteOwnerProductAPI, 
  clearAllOwnerProductsAPI 
} from '../api/bicycle';
import { getCompleteOwnerInfo } from '../utils/localStorage';

// Types & Mocks
import { Product, OwnerInfo } from '../types';
import { getProductByUrcode } from '../mocks/products';

const OwnerListPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [ownerInfo, setOwnerInfo] = useState<OwnerInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    urcode: '',
    productName: ''
  });
  const [clearAllModal, setClearAllModal] = useState(false);

  const loadOwnerProducts = useCallback(async () => {
    try {
      setLoading(true);
      
      // 로컬스토리지에서 완전한 owner 정보 가져오기
      const storedOwnerInfo = getCompleteOwnerInfo();
      
      if (!storedOwnerInfo) {
        setError('Owner information not found.');
        return;
      }
      
      setOwnerInfo(storedOwnerInfo);

      // 소유 제품 목록 API 호출
      const response = await getOwnerProductsAPI(storedOwnerInfo.ownerId);
      const { urItems } = response;

      // 각 urcode에 대한 제품 정보 가져오기
      const productList: Product[] = [];
      for (const urcode of urItems) {
        const product = getProductByUrcode(urcode);
        if (product) {
          productList.push(product);
        }
      }

      setProducts(productList);
    } catch (error) {
      console.error('Error loading owner products:', error);
      setError('Failed to load product list.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOwnerProducts();
  }, [loadOwnerProducts]);

  // 페이지가 다시 포커스될 때마다 데이터 새로고침
  useEffect(() => {
    const handleFocus = () => {
      loadOwnerProducts();
    };

    window.addEventListener('focus', handleFocus);
    
    // visibilitychange 이벤트도 추가 (모바일 브라우저 대응)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadOwnerProducts();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadOwnerProducts]);

  const handleProductClick = (urcode: string) => {
    // owner-list에서 왔다는 파라미터를 추가하여 이동
    navigate(`/bicycle/${urcode}?from=owner-list`);
  };

  // 개별 삭제 핸들러
  const handleDeleteClick = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation(); // 부모 클릭 이벤트 방지
    setDeleteModal({
      isOpen: true,
      urcode: product.ur,
      productName: product.product.productName
    });
  };

  const confirmDelete = async () => {
    if (!ownerInfo || !deleteModal.urcode) return;

    try {
      await deleteOwnerProductAPI(ownerInfo.ownerId, deleteModal.urcode);
      // 삭제 후 목록 새로고침
      await loadOwnerProducts();
      setDeleteModal({ isOpen: false, urcode: '', productName: '' });
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete bicycle. Please try again.');
    }
  };

  // 전체 삭제 핸들러
  const handleClearAll = () => {
    setClearAllModal(true);
  };

  const confirmClearAll = async () => {
    if (!ownerInfo) return;

    try {
      await clearAllOwnerProductsAPI(ownerInfo.ownerId);
      // 삭제 후 목록 새로고침
      await loadOwnerProducts();
      setClearAllModal(false);
    } catch (error) {
      console.error('Error clearing all products:', error);
      alert('Failed to clear all bicycles. Please try again.');
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingText>Loading...</LoadingText>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorText>{error}</ErrorText>
      </Container>
    );
  }

  const displayName = ownerInfo?.name || 'Jisoo Park';

  return (
    <Container>
      <ContentWrapper>
        <Header>
          <Title>My Registered Bicycles</Title>
          <ButtonGroup>
            {products.length > 0 ? (
              // 제품이 있을 때: Clear All만 표시
              <SmallButton onClick={handleClearAll}>Clear All</SmallButton>
            ) : (
              // 제품이 없을 때: Back만 표시
              <SmallButton onClick={() => navigate(-1)}>Back</SmallButton>
            )}
          </ButtonGroup>
        </Header>
        
        <UserProfile userName={displayName} />
        
        <ProductListContainer>
          {products.length === 0 ? (
            <EmptyMessage>
              No registered bicycles yet.
            </EmptyMessage>
          ) : (
            products.map((product) => (
              <ProductItem key={product.ur}>
                <ProductButton onClick={() => handleProductClick(product.ur)}>
                  <BrandLogoWrapper>
                    {product.product.brandLogo ? (
                      <BrandLogo src={product.product.brandLogo} alt={product.product.brand} />
                    ) : (
                      <DefaultIcon>🚲</DefaultIcon>
                    )}
                  </BrandLogoWrapper>
                  <ProductName>{product.product.productName}</ProductName>
                  <DeleteButton onClick={(e) => handleDeleteClick(e, product)}>
                    ✕
                  </DeleteButton>
                </ProductButton>
              </ProductItem>
            ))
          )}
        </ProductListContainer>
      </ContentWrapper>
      
      <URCodeFooter />
      
      {/* Delete Modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title="Delete Bicycle?"
        message={`Are you sure you want to delete "${deleteModal.productName}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal({ isOpen: false, urcode: '', productName: '' })}
      />
      
      {/* Clear All Modal */}
      <ConfirmModal
        isOpen={clearAllModal}
        title="Clear All Bicycles?"
        message="Are you sure you want to remove all registered bicycles? This action cannot be undone."
        confirmText="Clear All"
        onConfirm={confirmClearAll}
        onCancel={() => setClearAllModal(false)}
      />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #2595FF 0%, #69C8C9 100%);
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 120px;
`;

const Header = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SmallButton = styled.button`
  background: transparent;
  color: white;
  border: 2px solid white;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ProductListContainer = styled.div`
  width: 100%;
  max-width: 360px;
  margin-top: 32px;
`;

const ProductItem = styled.div`
  margin-bottom: 16px;
`;

const ProductButton = styled.button`
  width: 100%;
  padding: 16px 20px;
  background: transparent;
  color: white;
  border: 3px solid white;
  border-radius: 50px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const BrandLogoWrapper = styled.div`
  width: 56px;
  height: 56px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 8px;
`;

const BrandLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const DefaultIcon = styled.div`
  font-size: 32px;
`;

const ProductName = styled.span`
  flex: 1;
  text-align: left;
  padding-right: 40px; // 삭제 버튼을 위한 공간
  font-weight: 600;
`;

const DeleteButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  background: white;
  color: #FF4757;
  border: none;
  border-radius: 50%;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #FF4757;
    color: white;
    transform: translateY(-50%) scale(1.1);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  &:focus {
    outline: none;
  }
`;

const LoadingText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-top: 50vh;
`;

const ErrorText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-top: 50vh;
`;

const EmptyMessage = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-top: 48px;
`;

export default OwnerListPage;
